/**
 * Themed, context-sensitive mouse cursor. Swaps `document.body.style.cursor` to
 * a stone-age tool PNG based on what's under the pointer / the active UI mode.
 * Using the native OS cursor (CSS `url()`) means zero input lag; we only rewrite
 * the style string when the resolved state actually changes.
 *
 * Each cursor ships at two sizes: a 24px 1x (the displayed size — small, like a
 * normal cursor) and a 48px @2x. We hand both to CSS `image-set()` so hi-dpi
 * displays render the 2x crisply instead of upscaling the 24px and looking
 * blurry/pixelated. A plain `url()` of the 1x is the fallback for any engine that
 * doesn't support image-set in `cursor`.
 *
 * State is resolved each frame in main.ts (placement → cast → hover); this module
 * just owns the asset manifest, hotspots, preloading, and the DOM write.
 */

export type CursorState =
  | "default"
  | "attack"
  | "harvest-wood"
  | "harvest-stone"
  | "harvest-forage"
  | "build"
  | "cast"
  | "invalid";

// Click hotspots, in CSS px of the 24px (1x) cursor. Every tool's working point
// sits in the upper-left, so the hotspot is near the top-left corner; the basket
// (forage) has no point, so it gets a slightly inset hotspot. Tune by eye.
const HOTSPOTS: Record<CursorState, { x: number; y: number }> = {
  "default":        { x: 1, y: 1 },
  "attack":         { x: 1, y: 1 },
  "harvest-wood":   { x: 1, y: 1 },
  "harvest-stone":  { x: 1, y: 1 },
  "harvest-forage": { x: 4, y: 3 },
  "build":          { x: 1, y: 1 },
  "cast":           { x: 1, y: 1 },
  "invalid":        { x: 1, y: 1 },
};

const ALL_STATES = Object.keys(HOTSPOTS) as CursorState[];

const lo = (id: CursorState): string => `/assets/cursors/${id}.png`;
const hi = (id: CursorState): string => `/assets/cursors/${id}@2x.png`;

export interface CursorController {
  /** Set the active cursor; no-op if unchanged. */
  set(state: CursorState): void;
}

export function createCursorController(): CursorController {
  // Preload both sizes of every cursor so the first swap doesn't flash a blank.
  for (const id of ALL_STATES) {
    new Image().src = lo(id);
    new Image().src = hi(id);
  }

  let current: CursorState | null = null;

  function set(state: CursorState): void {
    if (state === current) return;
    current = state;
    const h = HOTSPOTS[state];
    // image-set picks the 2x on hi-dpi; the plain url() is the fallback chain,
    // then `auto` if the PNG can't load at all.
    document.body.style.cursor =
      `image-set(url(${lo(state)}) 1x, url(${hi(state)}) 2x) ${h.x} ${h.y}, ` +
      `url(${lo(state)}) ${h.x} ${h.y}, auto`;
  }

  return { set };
}
